import {
  FC,
  RefCallback,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";
import GenericControllerWithInput from "@/components/forms/inputsWithController/GenericController";
import { useTranslation } from "react-i18next";
import DropDown from "@/components/forms/inputs/dropDown";
import { InputWithRef } from "@/components/forms/inputsWithRef/inputWithRef";
import GenericJsonBuilder, { FieldOption } from "@/components/general/genericJsonBuilder";
import JsonEditor from "@/components/general/jsonEditor";
import KeyValueListBuilder from "@/components/general/keyValueListBuilder";
import { actionParameterMap } from "@/components/actionChain/actionChainCreateAndEdit";

const CUSTOM_VARS_KEY = "custom_vars";
const ADDITIONAL_TAGS_KEY = "additional_tags";

const sanitizeVariableName = (key: string) => key.replace(/[^a-zA-Z0-9_]/g, "");

interface AdditionalDataWithKeyValueListProps {
  value: any;
  onChange: (value: any) => void;
  fieldOptions: FieldOption[];
  builderKey: number;
  listKey: string;
  listLabel: string;
  listHint?: string;
  keyPlaceholder: string;
  valuePlaceholder: string;
  sanitizeKey?: (rawKey: string) => string;
}

const AdditionalDataWithKeyValueList: FC<
  AdditionalDataWithKeyValueListProps
> = ({
  value,
  onChange,
  fieldOptions,
  builderKey,
  listKey,
  listLabel,
  listHint,
  keyPlaceholder,
  valuePlaceholder,
  sanitizeKey,
}) => {
  const { t } = useTranslation();

  const safeValue = value && typeof value === "object" ? value : {};
  const { [listKey]: rawListValue, ...builderData } = safeValue;
  const listValue =
    rawListValue &&
    typeof rawListValue === "object" &&
    !Array.isArray(rawListValue)
      ? rawListValue
      : {};

  const latest = useRef({ builderData, listValue });
  latest.current = { builderData, listValue };

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const handleBuilderChange = useCallback(
    (updated: any) => {
      onChangeRef.current({
        ...(updated && typeof updated === "object" ? updated : {}),
        [listKey]: latest.current.listValue,
      });
    },
    [listKey],
  );

  const handleListChange = useCallback(
    (obj: Record<string, any>) => {
      onChangeRef.current({
        ...latest.current.builderData,
        [listKey]: obj,
      });
    },
    [listKey],
  );

  return (
    <>
      <GenericJsonBuilder
        key={builderKey}
        label={t("additional_data")}
        fieldOptions={fieldOptions}
        renderAs="form"
        initialData={builderData}
        onChange={handleBuilderChange}
        enableDragDrop={false}
        enableAddNewEntryButton={false}
        enableResetButton={false}
        enableDeleteButton={false}
      />
      <KeyValueListBuilder
        label={listLabel}
        hint={listHint}
        addButtonLabel={t("add")}
        keyPlaceholder={keyPlaceholder}
        valuePlaceholder={valuePlaceholder}
        value={listValue}
        onChange={handleListChange}
        sanitizeKey={sanitizeKey}
      />
    </>
  );
};

type ActionChainModalContentProps = {
  availableActions: any;
  registerValidationTarget: (
    name: "name" | "action" | "additional_data",
  ) => RefCallback<HTMLElement>;
};

const ActionChainModalContent: FC<ActionChainModalContentProps> = ({
  availableActions,
  registerValidationTarget,
}) => {
  const { t } = useTranslation();
  const [additionalDataKey, setAdditionalDataKey] = useState(0);
  const [currentEditorInstance, setCurrentEditorInstance] = useState<any>(null);

  const { control } = useFormContext();
  const actionValue = useWatch({ control, name: "action" });

  useEffect(() => {
    setAdditionalDataKey((prev) => prev + 1);
  }, [JSON.stringify(actionValue)]);

  let actionsWithPreConfiguredAdditionalDataOnceSelected: string[] = [];

  if (Object.keys(actionParameterMap).length > 0) {
    actionsWithPreConfiguredAdditionalDataOnceSelected = Object.entries(
      actionParameterMap,
    )
      .filter(([, value]) => Object.keys(value).length > 0)
      .map(([key]) => key);
  }

  const createTodoFieldOptions: FieldOption[] = [
    {
      key: "done",
      type: "boolean",
      hide: true,
    },
    {
      key: "name",
      key_alias: t("name"),
      type: "string",
    },
    {
      key: "type",
      key_alias: t("type"),
      type: "string",
      predefined_values_with_aliases: [
        { key: "text", alias: t("string") },
        { key: "upload", alias: t("upload_type") },
      ],
    },
    {
      key: "target",
      key_alias: t("target"),
      type: "object",
      renderValueInputToggle: true,
      fields: [
        {
          key: "link",
          key_alias: t("link_destination"),
          type: "string",
          dependencies: [
            {
              dependsOn: "type",
              condition: (value) => value === "upload",
              action: "hide",
            },
          ],
        },
        {
          key: "linkTitle",
          key_alias: t("link_title"),
          type: "string",
          dependencies: [
            {
              dependsOn: "type",
              condition: (value) => value === "upload",
              action: "hide",
            },
          ],
        },
        {
          key: "filename",
          key_alias: t("file_name_single"),
          type: "string",
          dependencies: [
            {
              dependsOn: "type",
              condition: (value) => value === "text",
              action: "hide",
            },
          ],
        },
        {
          key: "folder",
          key_alias: t("folder"),
          type: "string",
          dependencies: [
            {
              dependsOn: "type",
              condition: (value) => value === "text",
              action: "hide",
            },
          ],
        },
      ],
    },
    {
      key: "tag_set",
      key_alias: t("tag_set"),
      type: "array",
      hide: true,
    },
    {
      key: "description",
      key_alias: t("description"),
      type: "string",
    },
    {
      key: "due_date_in_days",
      key_alias: t("due_date_in_days"),
      type: "number",
    },
    {
      key: "lead_time_in_minutes",
      key_alias: t("lead_time_in_minutes"),
      type: "number",
    },
    {
      key: "create_for",
      key_alias: t("actionchain_create_for"),
      type: "string",
      allow_custom_input: false,
      predefined_values_with_aliases: [
        { key: "subject", alias: "Subject" },
        { key: "zustaendige_cs_mitarbeiter", alias: "CSM" },
        { key: "zustaendige_med_assistenz", alias: "Med. Assistenz" },
        { key: "zustaendiger_arzt", alias: "Arzt" },
        { key: "zustaendige_sifa", alias: "SiFa" },
      ],
    },
  ];

  const createTrackedServiceFieldOptions: FieldOption[] = [
    {
      key: "tracked_service_template_id",
      type: "string",
      key_alias: t("actionchain_tracked_service_select"),
      renderValueInputAsSearchableDropdown: true,
      searchableDropdownParameters: {
        apiEndpoint: "trackedServiceTemplate",
        apiResponseValueForJson: "name",
        apiResponseNameToBeDisplayedOnDropdown: "internal_name",
      },
    },
  ];

  const startChainFieldOptions: FieldOption[] = [
    {
      key: "id",
      type: "string",
      key_alias: t("actionchain"),
      renderValueInputAsSearchableDropdown: true,
      searchableDropdownParameters: {
        apiEndpoint: "actionChainTemplate",
        apiResponseValueForJson: "name",
        customSearchParameter: "name__icontains",
      },
    },
  ];

  const validateConditionFieldOptions: FieldOption[] = [
    {
      key: "conditions",
      type: "array",
      fields: [
        {
          key: "key",
          type: "string",
          use_multiline: true,
          wrappableInDoubleBraces: true,
        },
        {
          key: "type",
          type: "string",
          predefined_values: ["is", "is_not"],
        },
        {
          key: "value",
          type: "string",
        },
      ],
    },
  ];

  const createNoteFieldOptions: FieldOption[] = [
    {
      key: "text",
      key_alias: t("string"),
      type: "string",
    },
  ];

  const generatePdfFieldOptions: FieldOption[] = [
    {
      key: "template",
      key_alias: t("template"),
      type: "string",
      renderValueInputAsSearchableDropdown: true,
      searchableDropdownParameters: {
        apiEndpoint: "documentTemplate",
        apiResponseValueForJson: "name",
        customSearchParameter: "name__icontains",
        additionalSearchParameters: { type: "template" },
      },
    },
    {
      key: "filename_template",
      key_alias: t("filename_template"),
      type: "string",
      use_multiline: true,
    },
    {
      key: "allergien_vorhanden",
      type: "string",
      operators: ["is", "not", "gt", "lt", "in", "nin", "contains"],
      renderKeyAs: "textfield",
    },
  ];

  const sendEmailFieldOptions: FieldOption[] = [
    {
      key: "mail_template_name",
      key_alias: t("actionchain_mail_template_select"),
      type: "string",
      renderValueInputAsSearchableDropdown: true,
      searchableDropdownParameters: {
        apiEndpoint: "mailTemplate",
        apiResponseValueForJson: "name",
        customSearchParameter: "name__icontains",
      },
    },
  ];

  const getAdditionalDataFieldOptions = () => {
    const currentAction =
      typeof actionValue === "object" && actionValue !== null
        ? actionValue.value
        : actionValue;

    switch (currentAction) {
      case "create_todo":
        return createTodoFieldOptions;
      case "create_tracked_service":
        return createTrackedServiceFieldOptions;
      case "start_action_chain":
        return startChainFieldOptions;
      case "validate_condition":
        return validateConditionFieldOptions;
      case "create_note":
        return createNoteFieldOptions;
      case "generate_pdf":
        return generatePdfFieldOptions;
      case "send_email":
        return sendEmailFieldOptions;
      default:
        return [];
    }
  };

  const currentActionValue =
    typeof actionValue === "object" && actionValue !== null
      ? actionValue.value
      : actionValue;
  const isPreConfigured =
    actionsWithPreConfiguredAdditionalDataOnceSelected.includes(
      currentActionValue,
    );
  const validateAdditionalData = (value: any) => {
    const editorValue = !isPreConfigured
      ? currentEditorInstance?.getValue?.()
      : undefined;
    const valueToValidate =
      typeof editorValue === "string" ? editorValue : value;

    if (typeof valueToValidate !== "string") {
      return true;
    }

    try {
      JSON.parse(valueToValidate);
      return true;
    } catch {
      return t("invalid_json_error");
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div ref={registerValidationTarget("name")}>
        <GenericControllerWithInput
          control={control}
          name="name"
          rules={{
            required: {
              value: true,
              message: t("field_required"),
            },
          }}
          renderInput={({ onChange, ref, value }) => (
            <InputWithRef
              name="name"
              onChange={onChange}
              label={t("name")}
              ref={ref}
              value={value}
              mandatory={true}
            />
          )}
        />
      </div>
      <div ref={registerValidationTarget("action")}>
        <GenericControllerWithInput
          control={control}
          name="action"
          rules={{
            required: {
              value: true,
              message: t("field_required"),
            },
          }}
          renderInput={({ onChange, value }) => (
            <DropDown
              options={availableActions}
              onChange={onChange}
              name="action"
              label={t("actionchain_available_actions")}
              defaultValue={value}
              mandatory={true}
            />
          )}
        />
      </div>
      <div ref={registerValidationTarget("additional_data")}>
        <GenericControllerWithInput
          control={control}
          name="additional_data"
          rules={{
            validate: validateAdditionalData,
          }}
          renderInput={({ onChange, value }) => {
            const isEmptyJson =
              !value ||
              (typeof value === "object" && Object.keys(value).length === 0);
            const additionalDataFieldOptions = getAdditionalDataFieldOptions();

            if (currentActionValue === "generate_pdf") {
              return (
                <AdditionalDataWithKeyValueList
                  value={value}
                  onChange={onChange}
                  fieldOptions={generatePdfFieldOptions}
                  builderKey={additionalDataKey}
                  listKey={CUSTOM_VARS_KEY}
                  listLabel={t("actionchain_custom_variables")}
                  listHint={t("actionchain_custom_variables_hint")}
                  keyPlaceholder={t("actionchain_variable_name")}
                  valuePlaceholder={t("value")}
                  sanitizeKey={sanitizeVariableName}
                />
              );
            }

            if (currentActionValue === "send_email") {
              return (
                <AdditionalDataWithKeyValueList
                  value={value}
                  onChange={onChange}
                  fieldOptions={sendEmailFieldOptions}
                  builderKey={additionalDataKey}
                  listKey={ADDITIONAL_TAGS_KEY}
                  listLabel={t("additional_tags")}
                  keyPlaceholder={t("actionchain_tag_name")}
                  valuePlaceholder={t("value")}
                />
              );
            }

            const shouldUseJsonEditor =
              !isPreConfigured &&
              (additionalDataFieldOptions.length === 0 ||
                isEmptyJson ||
                currentEditorInstance);

            return shouldUseJsonEditor ? (
              <JsonEditor
                value={isPreConfigured ? {} : value}
                onSave={onChange}
                autoSave={true}
                label={t("additional_data")}
                onEditorMount={(editor) => {
                  setCurrentEditorInstance(editor);
                }}
              />
            ) : (
              <GenericJsonBuilder
                key={additionalDataKey}
                name="additional_data"
                label={t("additional_data")}
                fieldOptions={getAdditionalDataFieldOptions()}
                renderAs="form"
                initialData={value}
                onChange={onChange}
                enableDragDrop={false}
                enableAddNewEntryButton={false}
                enableResetButton={false}
                enableDeleteButton={false}
              />
            );
          }}
        />
      </div>
    </div>
  );
};

export default ActionChainModalContent;
